import React, { useState, useEffect } from 'react';
import { Menu, X, Terminal, ArrowRight, Sun, Moon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isDarkMode, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: 'About', href: '#about' },
    { name: 'Skills', href: '#skills' },
    { name: 'Experience', href: '#experience' },
    { name: 'Projects', href: '#projects' },
    { name: 'Achievements', href: '#achievements' }, // ✅ Added back
    { name: 'Contact', href: '#contact' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${
        isScrolled 
          ? 'py-3 bg-[#0a0a0a]/80 dark:bg-[#0a0a0a]/80 bg-white/80 border-white/10 dark:border-white/10 border-gray-200' 
          : 'py-5 bg-transparent border-transparent'
      } backdrop-blur-md`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <a 
          href="#hero" 
          className="group flex items-center gap-2 relative z-50"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div className="p-2 rounded-lg bg-white/5 border border-white/10 group-hover:border-[var(--brand-primary)] transition-colors duration-300">
            <Terminal size={20} className="text-[var(--brand-primary)]" />
          </div>
          <span className="text-xl font-bold tracking-tight text-black dark:text-white group-hover:text-[var(--brand-primary)] transition-colors duration-300">
            Tushar<span className="text-[var(--brand-primary)] group-hover:text-black dark:group-hover:text-white">.dev</span>
          </span>
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6 lg:gap-8">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              className="text-sm font-medium text-gray-400 hover:text-white dark:text-gray-400 dark:hover:text-white transition-colors duration-300 relative group"
            >
              {link.name}
              <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-[var(--brand-primary)] transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
          
          <a
            href="#contact"
            className="group px-5 py-2.5 rounded-full text-sm font-bold transition-all duration-300 flex items-center gap-2"
            style={{
              background: 'var(--brand-primary)',
              color: isDarkMode ? '#000000' : '#000000',
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 0 20px rgba(0, 217, 255, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            Let's Talk
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </a>
        </nav>

        {/* Theme Toggle & Mobile Menu Button */}
        <div className="flex items-center gap-4">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg bg-white/5 border border-white/10 hover:border-[var(--brand-primary)] transition-colors duration-300"
            aria-label="Toggle theme"
          >
            {isDarkMode ? (
              <Sun size={20} className="text-[var(--brand-primary)]" />
            ) : (
              <Moon size={20} className="text-[var(--brand-primary)]" />
            )}
          </button>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden relative z-50 p-2 text-white dark:text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X size={24} className="text-[var(--brand-primary)]" />
            ) : (
              <Menu size={24} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div 
        className={`fixed inset-0 bg-[#0a0a0a] dark:bg-[#0a0a0a] bg-white z-40 flex flex-col items-center justify-center md:hidden transition-all duration-300 ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        }`}
      >
        {/* Decorative Background */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-[var(--brand-primary)]/10 blur-[80px] rounded-full" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#00FF88]/10 blur-[80px] rounded-full" />

        <nav className="flex flex-col items-center gap-8 relative z-10">
          {navLinks.map((link, index) => (
            <a
              key={link.name}
              href={link.href}
              className={`text-2xl font-bold text-gray-600 dark:text-gray-300 hover:text-[var(--brand-primary)] transition-all duration-300 ${
                isMobileMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {link.name}
            </a>
          ))}
          
          <a
            href="#contact"
            className={`mt-4 px-8 py-3 rounded-full font-bold text-black dark:text-black bg-[var(--brand-primary)] hover:bg-white dark:hover:bg-gray-200 transition-all duration-300 flex items-center gap-2 ${
              isMobileMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
            style={{ transitionDelay: '600ms' }}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Let's Talk <ArrowRight size={20} />
          </a>
        </nav>
      </div>
    </header>
  );
};

export default Header;